/**
 * WelcomeView (T-038, PR-07, DES-12, US-05).
 *
 * Transient welcome view rendered on first /portal entry after Eric has
 * reviewed the subscriber's onboarding. Shows once when:
 *   - onboarding_state.onboarding_status = 'eric_reviewed'
 *   - onboarding_state.eric_reviewed_at is set
 *   - onboarding_state.welcome_view_dismissed_at is null
 *
 * Content: Eric's avatar + the per-subscriber welcome block (composed by Eric
 * in the Phase F operator review queue, T-035, written to
 * onboarding_state.welcome_view_block) + two CTAs + the notifications
 * footnote. Dismiss persists server-side (welcome_view_dismissed_at) via the
 * subscriber-owned onboarding_state UPDATE policy. The same content surfaces
 * in /portal/notifications (T-047 dispatches a welcome_view_ready
 * notification; this component does not duplicate that, it only reads state).
 *
 * Coordination with Phase F: this component READS onboarding_state and writes
 * ONLY welcome_view_dismissed_at. It does not touch any Phase F file. If
 * Phase F has not yet shipped T-035 (welcome_view_block stays null), this
 * component renders nothing — it never blocks the dashboard.
 *
 * DES-12 layout: Eric avatar, "What I noticed in your context" block, two
 * CTAs (Go to your dashboard / Open context doc), notifications footnote.
 */

import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  dismissWelcomeView,
  fetchOwnOnboardingState,
  type PortalOnboardingState,
} from '@/lib/portal';

type WelcomeState =
  | { kind: 'hidden' }
  | { kind: 'shown'; block: string };

export function WelcomeView() {
  const [state, setState] = useState<WelcomeState>({ kind: 'hidden' });

  useEffect(() => {
    let cancelled = false;
    fetchOwnOnboardingState()
      .then((ob: PortalOnboardingState | null) => {
        if (cancelled || !ob) return;
        const ready =
          ob.onboarding_status === 'eric_reviewed' &&
          ob.eric_reviewed_at !== null &&
          ob.welcome_view_dismissed_at === null &&
          typeof ob.welcome_view_block === 'string' &&
          ob.welcome_view_block.trim().length > 0;
        if (ready) {
          setState({ kind: 'shown', block: ob.welcome_view_block as string });
        }
      })
      .catch(() => {
        // Non-fatal: if onboarding_state cannot be read, the dashboard still
        // renders. The welcome view is additive.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === 'hidden') return null;

  async function handleDismiss() {
    // Optimistic: hide immediately, persist in the background. The flag is
    // idempotent server-side (the update is guarded on
    // welcome_view_dismissed_at IS NULL).
    setState({ kind: 'hidden' });
    try {
      await dismissWelcomeView();
    } catch {
      // If the persist fails, the worst case is the view shows again on the
      // next load. No data loss; no user-facing error needed here.
    }
  }

  return (
    <section
      className="mx-auto w-full max-w-xl"
      aria-label="Welcome message from Eric"
    >
      {/* From-Eric header: avatar, sender line, unread dot. */}
      <div className="mb-6 flex items-center gap-3">
        <EricAvatar />
        <div className="min-w-0">
          <p className="text-sm font-medium text-[color:var(--color-navy)]">
            Eric, Yes Please Studio
          </p>
          <p className="text-xs text-[color:var(--color-text-mute)]">
            A note before your first task
          </p>
        </div>
        <span
          className="ml-auto h-2 w-2 shrink-0 rounded-full bg-[color:var(--color-navy)]"
          aria-hidden="true"
        />
      </div>

      {/* The letter. */}
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-white)] p-6 shadow-[var(--shadow-md)] sm:p-10">
        <h2 className="mb-7 font-display text-h2 italic leading-tight text-[color:var(--color-navy)]">
          I&apos;ve read everything.
        </h2>
        {/* Eric-authored, per-subscriber block (composed in the operator review
            queue). Rendered verbatim with a left-border "what I noticed" frame
            to match the DES-12 prototype. Do not transform the copy. */}
        <div className="rounded-md border-l-[3px] border-[color:var(--color-navy)] bg-[color:var(--color-linen)] p-5">
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
            What I noticed in your context
          </p>
          <div className="space-y-4 whitespace-pre-line text-base leading-relaxed text-[color:var(--color-navy)]">
            {state.block}
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Link
          to="/portal"
          onClick={handleDismiss}
          className="inline-flex items-center justify-center rounded-md bg-[color:var(--color-rose)] px-[30px] py-3 text-base font-medium text-[color:var(--color-navy)] transition-colors hover:bg-[color:var(--color-rose-dark)]"
        >
          Go to your dashboard
        </Link>
        <Link
          to="/portal/context"
          onClick={handleDismiss}
          className="inline-flex items-center justify-center rounded-md border-[1.5px] border-[color:var(--color-line-dark)] bg-transparent px-[22px] py-2.5 text-sm font-medium text-[color:var(--color-navy)] transition-colors hover:bg-[color:var(--color-linen)]"
        >
          Open context doc
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          className="inline-flex items-center justify-center rounded-md px-3 py-2 text-sm text-[color:var(--color-text-mute)] transition-colors hover:text-[color:var(--color-navy)]"
        >
          Dismiss
        </button>
      </div>
      <p className="mt-4 text-xs text-[color:var(--color-text-mute)]">
        This message is saved in your notifications feed.
      </p>
    </section>
  );
}

function EricAvatar() {
  // The actual headshot asset (OQ-01, NEEDS INPUT) is not wired yet. Until it
  // lands, render a round navy monogram (matching the prototype's "E" avatar)
  // so layout and a11y are correct now.
  return (
    <div
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-navy)] text-sm font-semibold text-[color:var(--color-linen)]"
      aria-label="Eric Stein-Beldring"
      role="img"
    >
      E
    </div>
  );
}
